// @ts-nocheck
import React, { useEffect, useImperativeHandle, useRef, useState, useCallback, forwardRef } from "react";
import ReactDOM from "react-dom";

export type AnchorRect = DOMRect | HTMLElement | null;

export interface PopoverProps {
    anchorRect: AnchorRect;
    open: boolean;
    onClose: () => void;
    children: React.ReactNode;
    padding?: number;
    placement?: "top" | "bottom" | "left" | "right" | "auto";
    mousePos?: { x: number; y: number } | null;
}

const resolveAnchorRect = (anchorRect: AnchorRect): DOMRect | null => {
    if (!anchorRect) return null;

    if (anchorRect instanceof HTMLElement || (anchorRect as any).getBoundingClientRect) {
        return (anchorRect as any).getBoundingClientRect();
    }

    return anchorRect as DOMRect;
};


/* ---------------------------
   Position Calculator
---------------------------- */
const computePosition = (
    anchor: DOMRect,
    popRect: DOMRect,
    padding: number,
    requestedPlacement: PopoverProps["placement"],
    mousePos?: { x: number; y: number } | null
) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
    const anchorCenterX = anchor.left + anchor.width / 2;
    const anchorCenterY = anchor.top + anchor.height / 2;
    const horizontalReference = mousePos?.x ?? anchorCenterX;
    const verticalReference = mousePos?.y ?? anchorCenterY;

    const placements = {
        top: {
            fits: anchor.top - padding >= popRect.height,
            top: anchor.top - popRect.height - padding,
            left: clamp(horizontalReference - popRect.width / 2, padding, vw - popRect.width - padding),
        },
        bottom: {
            fits: vh - anchor.bottom - padding >= popRect.height,
            top: anchor.bottom + padding,
            left: clamp(horizontalReference - popRect.width / 2, padding, vw - popRect.width - padding),
        },
        right: {
            fits: vw - anchor.right - padding >= popRect.width,
            top: clamp(verticalReference - popRect.height / 2, padding, vh - popRect.height - padding),
            left: anchor.right + padding,
        },
        left: {
            fits: anchor.left - padding >= popRect.width,
            top: clamp(verticalReference - popRect.height / 2, padding, vh - popRect.height - padding),
            left: anchor.left - popRect.width - padding,
        },
    } as const;

    const isAnchorRelevant = anchor.right > 0 && anchor.left < vw && anchor.bottom > 0 && anchor.top < vh;
    if (!isAnchorRelevant) return null;

    const fallbackOrder: Array<keyof typeof placements> = ["top", "bottom", "right", "left"];
    const order: Array<keyof typeof placements> =
        requestedPlacement && requestedPlacement !== "auto"
            ? [requestedPlacement, ...fallbackOrder.filter((item) => item !== requestedPlacement)]
            : fallbackOrder;

    for (const key of order) {
        const p = placements[key];
        if (p.fits) {
            return { top: p.top, left: p.left, placement: key };
        }
    }

    for (const key of order) {
        const p = placements[key];
        return {
            top: clamp(p.top, padding, vh - popRect.height - padding),
            left: clamp(p.left, padding, vw - popRect.width - padding),
            placement: key
        };
    }

    const fallbackKey = requestedPlacement && requestedPlacement !== "auto" ? requestedPlacement : "top";
    const p = placements[fallbackKey];
    return {
        top: clamp(p.top, padding, vh - popRect.height - padding),
        left: clamp(p.left, padding, vw - popRect.width - padding),
        placement: fallbackKey
    };
};

/* ---------------------------
   Popover Component
---------------------------- */

function PopoverComponent(
    props: PopoverProps,
    ref: React.Ref<HTMLDivElement>
): React.ReactElement | null {
    const { anchorRect, open, onClose, children, padding = 8, placement = "auto", mousePos } = props;

    const popRef = useRef<HTMLDivElement | null>(null);
    useImperativeHandle(ref, () => popRef.current as HTMLDivElement);

    const [mounted, setMounted] = useState(false);
    const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

    const closePopover = useCallback(() => {
        setPos(null);
        onClose();
    }, [onClose]);

    useEffect(() => {
        setMounted(true);
        return () => setMounted(false);
    }, []);

    const updatePosition = useCallback(() => {
        if (!anchorRect || !popRef.current) {
            setPos(null);
            return;
        }

        const currentAnchorRect = resolveAnchorRect(anchorRect);
        if (!currentAnchorRect) {
            setPos(null);
            return;
        }

        const popRect = popRef.current.getBoundingClientRect();
        const computed = computePosition(currentAnchorRect, popRect, padding, placement, mousePos);

        if (computed) setPos({ top: computed.top, left: computed.left });
        else setPos(null);
    }, [anchorRect, padding, placement, mousePos]);



    useEffect(() => {
        if (!open || !anchorRect) return;
        updatePosition();
    }, [open, anchorRect, updatePosition]);

    useEffect(() => {
        if (!open || !popRef.current) return;
        const observer = new ResizeObserver(() => requestAnimationFrame(updatePosition));
        observer.observe(popRef.current);
        return () => observer.disconnect();
    }, [open, updatePosition]);

    useEffect(() => {
        if (!open || !anchorRect) return;

        const currentAnchorRect = resolveAnchorRect(anchorRect);
        if (!currentAnchorRect) return;

        const fake = document.createElement("div");
        fake.style.position = "fixed";
        fake.style.top = `${currentAnchorRect.top}px`;
        fake.style.left = `${currentAnchorRect.left}px`;
        fake.style.width = `${currentAnchorRect.width}px`;
        fake.style.height = `${currentAnchorRect.height}px`;
        fake.style.pointerEvents = "none";
        fake.style.zIndex = "-1";
        document.body.appendChild(fake);

        const io = new IntersectionObserver((entries) => {
            const entry = entries[0];
            if (!entry.isIntersecting) closePopover();
            else requestAnimationFrame(updatePosition);
        });

        io.observe(fake);

        return () => {
            io.disconnect();
            fake.remove();
        };
    }, [open, anchorRect, closePopover, updatePosition]);

    useEffect(() => {
        if (!open || !anchorRect) return;

        let timer: number | null = null;

        const handleMouseMove = (e: MouseEvent) => {
            if (!popRef.current || !anchorRect) return;

            const { clientX, clientY } = e;
            const popRect = popRef.current.getBoundingClientRect();

            const currentAnchorRect = resolveAnchorRect(anchorRect);
            if (!currentAnchorRect) return;

            const inPop = clientX >= popRect.left && clientX <= popRect.right && clientY >= popRect.top && clientY <= popRect.bottom;
            const inAnchor =
                clientX >= currentAnchorRect.left && clientX <= currentAnchorRect.right && clientY >= currentAnchorRect.top && clientY <= currentAnchorRect.bottom;

            if (!inPop && !inAnchor) {
                if (timer) clearTimeout(timer);
                timer = window.setTimeout(closePopover, 60);
            } else if (timer) {
                clearTimeout(timer);
                timer = null;
            }
        };

        window.addEventListener("mousemove", handleMouseMove);
        return () => {
            window.removeEventListener("mousemove", handleMouseMove)
            if (timer) clearTimeout(timer);
        };
    }, [open, anchorRect, closePopover]);


    useEffect(() => {
        if (!open) return;

        const onScroll = (e: Event) => {
            const target = e.target as HTMLElement;
            // Close popover on scroll unless it's a scroll within the popover itself
            if (popRef.current && !popRef.current.contains(target)) {
                requestAnimationFrame(closePopover);
            }
        };

        const onResize = () => requestAnimationFrame(closePopover);

        window.addEventListener("scroll", onScroll, true);
        window.addEventListener("resize", onResize);

        return () => {
            window.removeEventListener("scroll", onScroll, true);
            window.removeEventListener("resize", onResize);
        };
    }, [open, closePopover]);


    if (!mounted) return null;

    const node = (
        <div
            ref={popRef}
            style={{
                position: "fixed",
                top: pos?.top ?? 0,
                left: pos?.left ?? 0,
                zIndex: pos ? 9999 : 0,
                opacity: pos ? 1 : 0,
                pointerEvents: pos ? 'auto' : "none",
            }}
        >
            {children}
        </div>
    );

    return ReactDOM.createPortal(node, document.body);
}

// Perfect forwardRef typing
const Popover = forwardRef<HTMLDivElement, PopoverProps>(PopoverComponent);

export default Popover;
